import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { StatusPanel } from "../components/StatusPanel";
import {
  approveSopVersion, getWorkflowHealth, listLaunchFlags, listSopVersions, setLaunchFlag,
} from "../lib/operationsService";
import "../member-final.css";

const starterPayload = {
  key: "closer-qualification-booking",
  title: "Closer qualification and booking",
  route: "closer",
  outcome: "qualified_call",
  summary: "Qualify the lead, prepare the outreach, and submit proof of a booked call.",
  metadata: {
    product: {
      productName: "InferenceSaver",
      offer: "AI cost audit and savings demo",
      targetCustomer: "B2B founders with rising inference spend",
      objective: "Book one qualified demo call.",
    },
    compensation: {
      type: "milestone",
      fixedUsd: 150,
      capUsd: 150,
      managerOverridePercent: 5,
      reviewerBonusUsd: 5,
      proofKey: "booking-proof",
      description: "Paid when the booked call proof is approved.",
    },
    cellName: "Closer Cell Alpha",
  },
  defaultReward: { min: 50, max: 150, currency: "USD" },
  minimumLevel: 4,
  minimumTrust: 60,
  claimHours: 24,
  capacity: 20,
  nodes: [
    {
      key: "research",
      title: "Research and qualify",
      instructions: "Confirm fit, decision-maker identity, and one specific buying signal.",
      inputType: "text",
      required: true,
      evidenceRequirements: [{ key: "qualification-note", kind: "text", label: "Qualification note" }],
      resources: [],
    },
    {
      key: "book",
      title: "Book the qualified call",
      instructions: "Use the approved outreach and submit the booking proof.",
      inputType: "check",
      required: true,
      evidenceRequirements: [{ key: "booking-proof", kind: "screenshot", label: "Booking confirmation" }],
      resources: [],
    },
  ],
  edges: [{ from: "research", to: "book" }],
  reviewRubric: [
    { key: "fit", label: "Qualified fit", weight: 50 },
    { key: "proof", label: "Valid booking proof", weight: 50 },
  ],
  resources: [],
};

export function WorkflowAdminPage() {
  const client = useQueryClient();
  const [selectedVersion, setSelectedVersion] = useState("");
  const [payload, setPayload] = useState(JSON.stringify(starterPayload, null, 2));
  const [notice, setNotice] = useState("");
  const flags = useQuery({ queryKey: ["launch-flags"], queryFn: listLaunchFlags });
  const sops = useQuery({ queryKey: ["sop-versions"], queryFn: listSopVersions });
  const health = useQuery({ queryKey: ["workflow-health"], queryFn: getWorkflowHealth });
  const parsed = useMemo(() => {
    try { return { value: JSON.parse(payload), error: "" }; }
    catch { return { value: null, error: "Structured JSON is invalid." }; }
  }, [payload]);
  const toggle = useMutation({
    mutationFn: ({ key, enabled }: { key: string; enabled: boolean }) => setLaunchFlag(key, enabled),
    onSuccess: async () => client.invalidateQueries({ queryKey: ["launch-flags"] }),
  });
  const approve = useMutation({
    mutationFn: () => approveSopVersion(selectedVersion, parsed.value),
    onSuccess: async () => {
      setNotice("SOP approved and a new immutable playbook version was created.");
      await Promise.all([
        client.invalidateQueries({ queryKey: ["sop-versions"] }),
        client.invalidateQueries({ queryKey: ["workflow-health"] }),
      ]);
    },
  });
  const error = flags.error ?? sops.error ?? health.error ?? toggle.error ?? approve.error;

  return (
    <div className="member-app standalone-member-page">
      <header className="member-header">
        <Link className="member-logo" to="/admin"><span>H</span><b>WORKFLOW OPERATIONS</b></Link>
        <div className="member-header-actions"><Link to="/admin/qa">QA CONSOLE</Link><Link to="/admin/cells">CELLS</Link><Link to="/admin/payouts">PAYOUTS</Link><Link to="/admin">ADMIN HOME</Link></div>
      </header>
      <main>
        <section className="standalone-member-hero">
          <span>TWENTY → SOP → SUPABASE</span>
          <h1>One source.<br />Controlled execution.</h1>
          <p>Twenty owns the opportunity and source SOP. Approved playbook versions drive the member task UI, evidence rules, reviews, and earnings.</p>
        </section>
        {notice && <StatusPanel title="Workflow updated" message={notice} kind="success" />}
        {error && <StatusPanel title="Operations action failed" message={error.message} kind="error" />}
        <section className="earnings-cards">
          <div><span>WORKFLOWS</span><strong>{health.data?.total ?? 0}</strong><small>{health.data?.live ?? 0} LIVE</small></div>
          <div><span>INVALID</span><strong>{health.data?.invalid ?? 0}</strong><small>NEED REPAIR</small></div>
          <div><span>OUTBOX</span><strong>{health.data?.outboxPending ?? 0}</strong><small>PENDING</small></div>
          <div><span>FAILED SYNC</span><strong>{health.data?.outboxFailed ?? 0}</strong><small>RETRY REQUIRED</small></div>
        </section>
        <section className="workflow-admin-grid">
          <div className="workflow-admin-panel">
            <div className="member-section-head"><div><span>RELEASE GATES</span><h2>Launch controls</h2></div></div>
            <p className="member-muted">Leave paid tasks and payouts off until the production secrets, real SOPs, Discord roles, and payout process are verified.</p>
            <div className="launch-flag-list">
              {(flags.data ?? []).map((flag) => (
                <label key={flag.key}>
                  <div><b>{flag.key.replaceAll("_", " ")}</b><span>{flag.description}</span></div>
                  <input type="checkbox" checked={flag.enabled} disabled={toggle.isPending}
                    onChange={(event) => toggle.mutate({ key: flag.key, enabled: event.target.checked })} />
                </label>
              ))}
            </div>
          </div>
          <div className="workflow-admin-panel">
            <div className="member-section-head"><div><span>HUMAN APPROVAL</span><h2>SOP versions</h2></div></div>
            <div className="sop-version-list">
              {(sops.data ?? []).map((sop) => (
                <button className={selectedVersion === sop.id ? "active" : ""} key={sop.id} onClick={() => setSelectedVersion(sop.id)}>
                  <div><b>{sop.title}</b><span>VERSION {sop.version} · {sop.status.replace("_", " ")}</span></div>
                  {sop.sourceUrl && <a href={sop.sourceUrl} target="_blank" rel="noreferrer" onClick={(event) => event.stopPropagation()}>SOURCE PDF</a>}
                </button>
              ))}
            </div>
          </div>
        </section>
        <section className="workflow-admin-panel structured-editor">
          <div className="member-section-head"><div><span>VERSIONED TASK GRAPH</span><h2>Approve structured SOP</h2></div></div>
          <p className="member-muted">Review every instruction, resource, evidence requirement, reward, and dependency before approval. Approval creates a new playbook version; it never mutates work already claimed.</p>
          <textarea aria-label="Structured SOP JSON" spellCheck={false} value={payload} onChange={(event) => setPayload(event.target.value)} />
          {parsed.error && <StatusPanel title="Cannot approve" message={parsed.error} kind="error" />}
          <button className="payout-submit" disabled={!selectedVersion || !parsed.value || approve.isPending} onClick={() => approve.mutate()}>
            {approve.isPending ? "APPROVING…" : "APPROVE AND VERSION PLAYBOOK"}
          </button>
        </section>
      </main>
    </div>
  );
}
